import { useMemo } from 'react'
import { atom, useAtom } from 'jotai'
import { atomWithDefault } from 'jotai/vanilla/utils'
import { scopeToString } from '../../scope'
import {
  findLastActiveIdFromLocalStorage,
  getEmptyViewStateForScope,
  globalAtom,
  viewStateStore,
} from '../../store'
import { useViewStateType } from '../view-state-type'
import type { Scope } from '../../types'

/**
 * Provides base access to the global set of view states + defaults.
 * Always gain access via this func as it ensures the given scope has the
 * required entries' setup.
 */
export const useViewStateBaseAtom = (scope: Scope) => {
  const key = scopeToString(scope)

  const [get, set] = useAtom(globalAtom, { store: viewStateStore })

  const type = useViewStateType(scope)

  // Initialise the current scope in the global atom.
  return useMemo(() => {
    if (key in get) {
      return get[key]
    }

    // These atoms should ideally not be returning the empty view state by default.
    // They should be undefined, then returning the value once it is returned by
    // the API, but changing it impacts lots of different places in the codebase.
    const emptyViewState = getEmptyViewStateForScope(type, scope)

    // Init default atom with an empty view state.
    const defaultAtom = atom({ ...emptyViewState })
    defaultAtom.debugLabel = `viewstate.scope(${key})`

    // Init atom for scope.
    const currentAtom = atomWithDefault((get) => get(defaultAtom))
    currentAtom.debugLabel = `viewstate.scope(${key}).withDefault`

    const preferredDefaultIdAtom = atom<string | undefined>(undefined)
    const lastActiveIdAtom = atom<string | undefined>(
      findLastActiveIdFromLocalStorage(scope),
    )

    const res = {
      default: defaultAtom,
      current: currentAtom,
      preferredDefaultId: preferredDefaultIdAtom,
      lastActiveId: lastActiveIdAtom,
    }

    // Add atoms to global atom.
    set((prev) => {
      return {
        ...prev,
        [key]: res,
      }
    })

    return res
  }, [scope, key, set, type, get])
}

/**
 * Returns an atom referencing the active view state for the given scope.
 */
export const useViewStateActiveAtom = (scope: Scope) =>
  useViewStateBaseAtom(scope)?.current

/**
 * Returns an atom referencing the default view state for the given scope.
 */
export const useViewStateDefaultAtom = (scope: Scope) =>
  useViewStateBaseAtom(scope)?.default

/**
 * Returns an atom referencing the preferred default view state for the given
 * scope.
 */
export const useViewStatePreferredDefaultAtom = (scope: Scope) =>
  useViewStateBaseAtom(scope)?.preferredDefaultId

/**
 * Returns an atom referencing the last active view state for the given scope.
 */
export const useViewStateLastActiveAtom = (scope: Scope) =>
  useViewStateBaseAtom(scope)?.lastActiveId
